import React, { useEffect, useRef, useState } from "react";

import Logo from "@assets/logos/logo.svg";
import PaySonicLogo from "@assets/logos/PaySonic.svg";
import PaysonicNoise from "@assets/noise/paysonic-noise.svg";
import SignUpNoise from "@assets/noise/tylt-noise.svg";
import CustomButton from "@components/buttons/CustomButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { getApiErrorDisplay, getRawApiMessage } from "@services/api/apiError";
import { authenticationApiService } from "@services/apis/Authentication";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import {
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import { OTPFormField } from "../../authentication/components/otp/OTPFormField/OTPFormField";

const MobileVerifyOtp = () => {
  const methods = useForm({
    defaultValues: {
      otp: Array(6).fill(""),
    },
  });

  const navigate = useNavigate();
  const location = useLocation();
  let email = (location.state as { email?: string })?.email ?? "";
  let csrfToken = (location.state as { csrfToken?: string })?.csrfToken ?? "";

  const loginEmail = email ? secureLocalStorage.getItem("loginEmail") : "";
  const loginCsrfToken = csrfToken
    ? secureLocalStorage.getItem("loginCsrfToken")
    : "";

  useEffect(() => {
    if (!loginEmail || !loginCsrfToken) {
      navigate("/login");
    }
  }, [loginEmail, loginCsrfToken, navigate]);

  const validLoginCsrfToken =
    typeof loginCsrfToken === "string" ? loginCsrfToken : "";

  const [token, setToken] = useState<string>(validLoginCsrfToken || csrfToken);

  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const [seconds, setSeconds] = useState(30);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();

  const timeSetting = () => {
    const interval = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);

    return interval;
  };

  useEffect(() => {
    const interval = timeSetting();
    return () => clearInterval(interval);
  }, []);

  const handleResendClick = () => {
    methods.reset({
      otp: Array(6).fill(""),
    });
    clearErrors("otp");
    setSeconds(30);
    timeSetting();
    handleResend();
  };

  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: { otp: any[] }) => {
    setIsButtonLoading(true);

    const code = data.otp.join("");

    try {
      const response = await authenticationApiService.verifyOtp(
        email,
        code,
        token,
      );
      secureLocalStorage.setItem("jwt", response.data.jwt);
      secureLocalStorage.removeItem("loginEmail");
      secureLocalStorage.removeItem("loginCsrfToken");
      navigate("/");
      window.location.reload();
    } catch (error: any) {
      const msg = getRawApiMessage(error);
      if (msg === "Access Denied!") {
        navigate("/error");
      } else {
        handleSetOtpErrorMessage(getApiErrorDisplay(error));
      }
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleSetOtpErrorMessage = (message: any) => {
    if (message) {
      setError("otp", {
        type: "manual",
        message,
      });
    } else {
      clearErrors("otp");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      continueButtonRef.current?.click();
    }
  };

  const checkValidOtp = () => {
    const data = methods.getValues();
    const code = data.otp.join("");
    return code.length === 6;
  };

  const handleResend = async () => {
    setIsButtonLoading(true);

    try {
      const data = await authenticationApiService.sendOtp(email);
      setToken(data.data.csrfToken);
    } catch (error: any) {
      handleSetOtpErrorMessage(getApiErrorDisplay(error));
    } finally {
      setIsButtonLoading(false);
    }
  };

  const checkLogin = () => {
    const jwt = secureLocalStorage.getItem("jwt");
    if (jwt) {
      navigate("/");
    }
  };

  useEffect(() => {
    checkLogin();
  }, []);

  return (
    <div className="flex flex-col bg-[#ffffff] min-h-screen">
      {/* Blue Carousel Banner — Top */}
      <div
        className="w-full bg-text-navbar-active py-10 px-6"
        style={{
          backgroundImage: `url(${isPaysonic ? PaysonicNoise : SignUpNoise})`,
        }}
      >
        <div className="flex flex-col items-center text-[#ffffff]">
          <div className="font-bold text-[22px] leading-[34px] text-center">
            <p>
              {isPrimeBrazilOnly
                ? "ADMIN PANEL"
                : isPaysonic
                  ? "PAYSONIC ADMIN PANEL"
                  : "TYLT ADMIN PANEL"}
            </p>
            <p>
              MANAGE OPERATIONS <br /> WITH EASE.
            </p>
          </div>
          <div className="text-center mt-3 px-4">
            <p className="font-medium text-[13px] leading-[20px]">
              Manage your operations efficiently. Monitor transactions, oversee
              users, and configure platform settings with comprehensive admin
              tools.
            </p>
          </div>
        </div>
      </div>

      {/* Form Section — Below */}
      <div className="flex flex-col justify-start items-center px-4 pt-8 flex-1">
        <div className="flex flex-row mb-[24px] items-center justify-center">
          <img
            src={isPaysonic ? PaySonicLogo : Logo}
            alt="logo"
            className="h-24 w-24"
          />
        </div>
        <div className="p-[16px] w-full max-w-[400px] rounded-[12px]">
          <div className="flex items-center justify-between mb-4 relative">
            <span
              className="material-symbols-outlined absolute left-0 cursor-pointer"
              onClick={() => navigate("/mobile/login")}
            >
              arrow_back_ios
            </span>
            <div className="text-text-base text-[14px] font-semibold flex-grow text-center">
              Start your admin journey
            </div>
          </div>

          <div className="text-text-base text-[14px] mt-4 font-normal mb-4">
            Enter the verification code we've sent to your email ID
            <br />
            {email}.
          </div>
          <div className="border-[0.5px] border-border-table"></div>

          <FormProvider {...methods}>
            <form onKeyDown={handleKeyDown}>
              <OTPFormField
                name="otp"
                onSetOtpErrorMessage={handleSetOtpErrorMessage}
                length={6}
                errorMessage={errors.otp && errors.otp.message}
              />
              <div className="flex justify-between items-center mt-3">
                <span className="text-[12px] font-light">
                  Enter OTP in{" "}
                  <span className="text-text-brand-alt font-semibold text-[14px]">
                    {seconds}s
                  </span>
                </span>
                <div className="mt-2">
                  <LightCustomButton
                    text="REGENERATE OTP"
                    onClick={handleResendClick}
                    isActive={seconds <= 0}
                    isButtonLoading={isButtonLoading}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
          <div className="mt-7 my-4">
            <CustomButton
              ref={continueButtonRef}
              text="SUBMIT"
              onClick={handleSubmit(onSubmit)}
              isActive={checkValidOtp()}
              isButtonLoading={isButtonLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileVerifyOtp;
